import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { Url } from '../services/Constants';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';

export default function DeleteUser() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Request, 2: Verify, 3: Success
  const [role, setRole] = useState('buyer'); // 'buyer' or 'seller'
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!mobileNumber) {
      toast.error('Please enter your mobile number');
      return;
    }
    if (!/^\d{10}$/.test(mobileNumber)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${Url}/api/v2/request_account_deletion`, {
        mobile_number: mobileNumber,
        role: role
      });

      if (response.data && (response.data.response_code === 200 || response.data.status === 200)) {
        toast.success(response.data.response_message || 'OTP sent successfully!');
        setStep(2);
      } else {
        toast.error(response.data.response_message || 'Failed to request OTP');
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.response_message || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndDelete = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${Url}/api/v2/verify_account_deletion`, {
        mobile_number: mobileNumber,
        role: role,
        otp: otp
      });

      if (response.data && (response.data.response_code === 200 || response.data.status === 200)) {
        toast.success(response.data.response_message || 'Account successfully deleted.');
        setStep(3);
      } else {
        toast.error(response.data.response_message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.response_message || 'Verification failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-neutral-100 transition-all duration-300">
          
          {step === 1 && (
            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-amber-950">Delete Account</h2>
                <p className="text-sm text-neutral-500 mt-2">
                  Request account deletion by verifying your mobile number
                </p>
              </div>

              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
                <h4 className="text-red-700 font-semibold text-sm">Warning: Permanent Action</h4>
                <p className="text-xs text-red-600 mt-1">
                  Deleting your account will permanently destroy your profile, order history, and all associated details. This action cannot be undone.
                </p>
              </div>

              <form onSubmit={handleRequestOtp} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-amber-950 mb-3">
                    Choose Your Role
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setRole('buyer')}
                      className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all duration-200 ${
                        role === 'buyer'
                          ? 'border-amber-950 bg-amber-950 text-white shadow-md'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      Buyer
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('seller')}
                      className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all duration-200 ${
                        role === 'seller'
                          ? 'border-amber-950 bg-amber-950 text-white shadow-md'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      Seller
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="mobile" className="block text-sm font-semibold text-amber-950 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-neutral-400 font-medium">
                      +91
                    </span>
                    <input
                      id="mobile"
                      type="tel"
                      maxLength="10"
                      placeholder="Enter 10-digit number"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-12 pr-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-950/20 focus:border-amber-950 transition-all"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-amber-950 hover:bg-amber-900 text-white rounded-xl text-sm font-semibold shadow-lg shadow-amber-950/20 active:scale-[0.98] transition-all flex justify-center items-center gap-2"
                >
                  {loading ? 'Sending OTP...' : 'Send Verification OTP'}
                </button>
              </form>
            </div>
          )}

          {step === 2 && (
            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-amber-950">Verify OTP</h2>
                <p className="text-sm text-neutral-500 mt-2">
                  Enter the 6-digit verification code sent to +91 {mobileNumber} ({role})
                </p>
              </div>

              <form onSubmit={handleVerifyAndDelete} className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-semibold text-amber-950 mb-2">
                    Enter OTP
                  </label>
                  <input
                    id="otp"
                    type="text"
                    maxLength="6"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-800 text-center tracking-[0.5em] text-lg font-bold focus:outline-none focus:ring-2 focus:ring-amber-950/20 focus:border-amber-950 transition-all"
                    disabled={loading}
                    required
                  />
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-neutral-500">Didn't receive code?</span>
                  <button
                    type="button"
                    onClick={handleRequestOtp}
                    disabled={loading}
                    className="text-amber-950 font-bold hover:underline"
                  >
                    Resend OTP
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-semibold shadow-lg shadow-red-600/20 active:scale-[0.98] transition-all flex justify-center items-center gap-2"
                >
                  {loading ? 'Verifying...' : 'Verify & Destroy Account'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={loading}
                  className="w-full text-center text-neutral-500 hover:text-neutral-700 text-xs font-semibold"
                >
                  Go Back
                </button>
              </form>
            </div>
          )}

          {step === 3 && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>

              <h2 className="text-3xl font-bold text-amber-950">Success</h2>
              <p className="text-neutral-600 mt-4 text-sm leading-relaxed">
                Your account for phone number +91 {mobileNumber} as a {role} has been successfully destroyed.
              </p>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-8 w-full py-3.5 bg-amber-950 hover:bg-amber-900 text-white rounded-xl text-sm font-semibold shadow-lg shadow-amber-950/20 transition-all"
              >
                Go to Home Page
              </button>
            </div>
          )}

        </div>
      </div>

      <Footer />

      {loading && (
        <div className="fixed inset-0 z-50 bg-neutral-900 bg-opacity-40 flex justify-center items-center backdrop-blur-[2px]">
          <Oval
            visible={true}
            height="80"
            width="80"
            color="#78350f"
            secondaryColor="none"
            strokeWidth={3}
          />
        </div>
      )}
    </div>
  );
}
